'use client';

import React, {
  useState,
  Children,
  cloneElement,
  isValidElement,
  useEffect
} from 'react';
import { PersonalInfoSection } from './sections/PersonalInfoSection';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Stack } from '@mui/material';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type UpdateProfileDetails,
  updateProfileSchema
} from '@/schemas/updateProfileSchema';

export const ManageAccount = ({
  userDetails,
  onSuccess,
  children
}: {
  userDetails: UserDetails;
  onSuccess?: () => void;
  children?: React.ReactNode;
}) => {
  const [messageText, setMessage] = useState<string | undefined>(undefined);
  const [errorText, setError] = useState<string | undefined>(undefined);

  const {
    formState: { isValid, errors, isSubmitting },
    register,
    control,
    handleSubmit,
    trigger
  } = useForm<UpdateProfileDetails>({
    defaultValues: {
      firstName: userDetails.firstName,
      lastName: userDetails.lastName,
      mobile: userDetails.mobile,
      company: userDetails.company,
      city: userDetails.city,
      position: userDetails.position,
      industry: userDetails.industry,
      website: userDetails.website,
      twitter: userDetails.twitter,
      instagram: userDetails.instagram,
      interests: userDetails.interests
    },
    mode: 'all',
    resolver: zodResolver(updateProfileSchema)
  });

  useEffect(() => {
    void trigger();
  }, [trigger]);

  const handleUpdateProfile = async (data: UpdateProfileDetails) => {
    if (isSubmitting) {
      return;
    }
    try {
      setMessage(undefined);
      setError(undefined);
      const result = await fetch(`/api/contact`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          contact: {
            id: userDetails.id,
            firstName: data.firstName,
            lastName: data.lastName,
            mobile: data.mobile,
            company: data.company,
            city: data.city,
            position: data.position,
            industry: data.industry,
            website: data.website,
            twitter: data.twitter,
            instagram: data.instagram,
            interests: data.interests
          }
        })
      });

      const response = (await result.json()) as {
        message?: string;
        error?: string;
      };

      if (response.message) {
        setMessage(response.message);
      }

      if (response.error) {
        setError(response.error);
      }
    } catch (e) {
      setError('Something went wrong. Please try again later.');
    }
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      updateProfileSchema.parse(data);
      await handleUpdateProfile(data);
      if (onSuccess) onSuccess();
    } catch (e) {
      setError('Something went wrong. Please try again later.');
    }
  });

  return (
    <form onSubmit={onSubmit}>
      <Stack spacing={2}>
        {children ? (
          Children.map(children, (child) =>
            isValidElement(child)
              ? cloneElement(child, {
                  userDetails,
                  register,
                  errors,
                  control
                })
              : child
          )
        ) : (
          <>
            <PersonalInfoSection
              userDetails={userDetails}
              register={register}
              errors={errors}
              control={control}
              showPersonalInfo={true}
            />
            {/* TODO: remove interests section if we are happy with including interests in the main form */}
            {/* <InterestsSection
                  userInterests={userDetails.interests}
                  register={register}
                  control={control}
                /> */}
          </>
        )}
        {messageText ? (
          <Alert severity='success' icon={false} sx={{ mt: '20px' }}>
            {messageText}
          </Alert>
        ) : null}
        {errorText ? (
          <Alert severity='error' icon={false} sx={{ mt: '20px' }}>
            {errorText}
          </Alert>
        ) : null}
        <div>
          <LoadingButton
            type='submit'
            loading={isSubmitting}
            className='btn-continue'
            disabled={!isValid}
            variant='contained'
            color='primary'
            disableElevation
          >
            Update Information
          </LoadingButton>
        </div>
      </Stack>
    </form>
  );
};
